import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { toast } from 'react-hot-toast';
import {
  productReleaseApi,
  type ReleaseSuggestion,
  type VersionBump,
} from '@/services/version/productReleaseApi';

interface Props {
  onClose: () => void;
  onReleased: () => void;
}

const BUMPS: VersionBump[] = ['patch', 'minor', 'major'];

function todayLocal(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function isStatus(err: unknown, status: number): boolean {
  if (typeof err !== 'object' || err === null || !('response' in err)) return false;
  const response = (err as { response?: { status?: number } }).response;
  return response?.status === status;
}

const ReleaseVersionDialog = ({ onClose, onReleased }: Props) => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState<ReleaseSuggestion | null>(null);
  const [bump, setBump] = useState<VersionBump>('patch');
  const [releasedOn, setReleasedOn] = useState(todayLocal());
  const [title, setTitle] = useState('');
  const [summarySv, setSummarySv] = useState('');
  const [summaryEn, setSummaryEn] = useState('');
  const [notesSv, setNotesSv] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    productReleaseApi.suggestion()
      .then((data) => {
        if (!active) return;
        setSuggestion(data);
        setBump(data.suggestedBump);
      })
      .catch(() => {
        if (active) toast.error(t('version.releaseLoadError'));
      });
    return () => {
      active = false;
    };
  }, [t]);

  const version = suggestion?.versions[bump] ?? '';

  const submit = async (confirmLower: boolean) => {
    const notes = notesSv
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((textSv) => ({ textSv, textEn: '' }));
    setSaving(true);
    try {
      await productReleaseApi.create({
        bump,
        releasedOn,
        title: title.trim(),
        summarySv: summarySv.trim(),
        summaryEn: summaryEn.trim(),
        notes,
        confirmLower,
      });
      toast.success(t('version.releaseSaved', { version }));
      onReleased();
    } catch (err: unknown) {
      if (!confirmLower && isStatus(err, 409)) {
        const ok = window.confirm(t('version.confirmLower'));
        if (ok) {
          setSaving(false);
          await submit(true);
          return;
        }
      } else {
        toast.error(t('version.releaseError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-sm font-medium text-textDefault mb-1';
  const inputCls =
    'w-full px-3 py-2 bg-bgContainer border border-border rounded-lg text-textDefault focus:border-primary focus:outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-bgElevated rounded-lg border border-border w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-textDefault">
            {t('version.releaseAction')}
            {version ? <span className="ml-2 font-mono text-primary">v{version}</span> : null}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-bgContainer rounded" type="button">
            <XMarkIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-textSecondary">
            {t('version.pendingCount', { count: suggestion?.pendingCount ?? 0 })}
          </p>
          <div>
            <label className={labelCls}>{t('version.level')}</label>
            <div className="flex gap-2">
              {BUMPS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setBump(level)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    bump === level ? 'border-primary bg-primary/10 text-primary' : 'border-border text-textDefault'
                  }`}
                >
                  {t(`version.bump.${level}`) || level}
                  {suggestion ? <span className="ml-1 font-mono">v{suggestion.versions[level]}</span> : null}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>{t('version.releaseDate') || 'Datum'}</label>
            <input className={inputCls} type="date" value={releasedOn} onChange={(e) => setReleasedOn(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('version.releaseTitle') || 'Rubrik'}</label>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('version.summary') || 'Kort text'}</label>
            <textarea className={inputCls} rows={2} value={summarySv} onChange={(e) => setSummarySv(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('version.summaryEn') || 'Kort text (engelska)'}</label>
            <textarea className={inputCls} rows={2} value={summaryEn} onChange={(e) => setSummaryEn(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('version.notes') || 'Punkter, en per rad'}</label>
            <textarea className={inputCls} rows={4} value={notesSv} onChange={(e) => setNotesSv(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-textDefault">
            {t('common.cancel') || 'Avbryt'}
          </button>
          <button
            type="button"
            disabled={saving || !suggestion}
            onClick={() => submit(false)}
            className="px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('version.releaseAction')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseVersionDialog;
